export const DEFAULT_WEB_MAKER_STYLE_PRESETS = Object.freeze({
  heading: Object.freeze({
    fontSize: "32px",
    fontWeight: "bold",
    lineHeight: 1.2,
    letterSpacing: "0px",
    textTransform: "none",
  }),
  body: Object.freeze({
    fontSize: "16px",
    fontWeight: "normal",
    lineHeight: 1.5,
    letterSpacing: "0px",
    textTransform: "none",
  }),
  button: Object.freeze({
    fontSize: "16px",
    fontWeight: "bold",
    lineHeight: 1.2,
    padding: "12px 24px",
    borderRadius: "8px",
  }),
  input: Object.freeze({
    fontSize: "16px",
    padding: "10px 12px",
    borderRadius: "6px",
  }),
  card: Object.freeze({
    padding: "16px",
    borderRadius: "12px",
    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)",
  }),
  spacing: Object.freeze({ base: 16 }),
  radius: Object.freeze({ small: 4, medium: 8, large: 16 }),
  shadows: Object.freeze({
    card: "0 8px 24px rgba(15, 23, 42, 0.12)",
    elevated: "0 14px 34px rgba(15, 23, 42, 0.18)",
  }),
});

const clone = (value) => JSON.parse(JSON.stringify(value));

export const normalizeGlobalStylePresets = (presets) => {
  const source = presets && typeof presets === "object" ? presets : {};
  return Object.fromEntries(
    Object.entries(DEFAULT_WEB_MAKER_STYLE_PRESETS).map(([key, defaults]) => [
      key,
      { ...clone(defaults), ...(source[key] || {}) },
    ])
  );
};

const TEXT_VISUAL_KEYS = Object.freeze([
  "fontFamily",
  "fontSize",
  "fontWeight",
  "fontStyle",
  "textDecoration",
  "color",
  "backgroundColor",
  "padding",
  "borderRadius",
  "boxShadow",
  "visualEffects",
  "lineHeight",
  "letterSpacing",
  "textTransform",
]);

export const resolveTextStylePreset = (style = {}, presets = {}) => {
  if (!style?.stylePreset) return { ...style };
  const normalizedPresets = normalizeGlobalStylePresets(presets);
  const preset = normalizedPresets[style.stylePreset] || {};
  const resolved = { ...style };
  TEXT_VISUAL_KEYS.forEach((key) => {
    const local = style[key];
    if (local === "" || local === null || local === undefined) {
      if (preset[key] !== undefined) resolved[key] = preset[key];
    }
  });
  return resolved;
};

export const clearTextLocalFormatting = (style = {}) => {
  const next = { ...style };
  TEXT_VISUAL_KEYS.forEach((key) => {
    next[key] = "";
  });
  next.animationType = "none";
  next.animationDuration = 600;
  next.animationDelay = 0;
  return next;
};

export const buildButtonPresetStyle = (button = {}, presets = {}) => {
  const normalized = normalizeGlobalStylePresets(presets);
  if (button.stylePreset !== "button") return {};
  const preset = normalized.button;
  return {
    ...(button.padding ? {} : { padding: preset.padding }),
    ...(button.borderRadius ? {} : { borderRadius: preset.borderRadius }),
    textStyle: {
      ...(button.textStyle?.fontSize ? {} : { fontSize: preset.fontSize }),
      ...(button.textStyle?.fontWeight ? {} : { fontWeight: preset.fontWeight }),
      ...(button.textStyle?.lineHeight ? {} : { lineHeight: preset.lineHeight }),
    },
  };
};

export const buildGlobalPresetCssVariables = (presets) => {
  const normalized = normalizeGlobalStylePresets(presets);
  return {
    "--wm-spacing-base": `${normalized.spacing.base}px`,
    "--wm-radius-small": `${normalized.radius.small}px`,
    "--wm-radius-medium": `${normalized.radius.medium}px`,
    "--wm-radius-large": `${normalized.radius.large}px`,
    "--wm-card-shadow": normalized.shadows.card,
    "--wm-elevated-shadow": normalized.shadows.elevated,
    "--wm-input-radius": normalized.input.borderRadius,
    "--wm-card-radius": normalized.card.borderRadius,
  };
};

export const TEXT_STYLE_PRESET_OPTIONS = Object.freeze(["", "heading", "body"]);
export const TEXT_SEMANTIC_ROLES = Object.freeze([
  "paragraph",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "unordered-list",
  "ordered-list",
]);

export const resolveSemanticTextTag = (role, fallback = "p") => {
  if (/^h[1-6]$/.test(role)) return role;
  if (role === "unordered-list") return "ul";
  if (role === "ordered-list") return "ol";
  if (role === "paragraph") return "p";
  return fallback;
};

export const resolveSafeLinkRel = ({ target, rel } = {}) => {
  const tokens = new Set(
    String(rel || "")
      .toLowerCase()
      .split(/\s+/)
      .filter((token) => ["nofollow", "sponsored", "ugc", "noopener", "noreferrer"].includes(token))
  );
  if (target === "_blank") {
    tokens.add("noopener");
    tokens.add("noreferrer");
  }
  return Array.from(tokens).join(" ");
};
